/**
 * Onboarding asks "are you a recent graduate?" rather than asking people to self-assess a seniority
 * band — the answer plus a date is something they actually know. The band the backend scores
 * against is derived from it here.
 *
 * The thresholds come from `ExperienceLevelOptions` in the service
 * (`src/job-market/job-market.constants.ts`), which documents the ranges alongside each value:
 *   Entry  < 1 yr | Junior 1–3 yrs | Mid 3–6 yrs | Senior 6+ yrs
 * Only these four strings match in `calculateSeniorityMatch`, so they must stay exact.
 */
export const SENIORITY_LEVELS = {
	entry: "Entry-level(0-1 yr)",
	junior: "Junior-level(1-2 yrs)",
	mid: "Mid-level(3-5 yrs)",
	senior: "Senior-level(6+ yrs)",
} as const;

export type SeniorityLevel = (typeof SENIORITY_LEVELS)[keyof typeof SENIORITY_LEVELS];

/**
 * Whole and part years between `month`/`year` (1-indexed month) and now. A date in the future —
 * an expected graduation — counts as zero rather than going negative.
 */
export function yearsSince(month: number, year: number, now: Date = new Date()): number {
	if (!Number.isFinite(month) || !Number.isFinite(year)) return 0;

	const elapsedMonths = (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month);
	return Math.max(0, elapsedMonths / 12);
}

/**
 * Map a career-start (or graduation) date onto the seniority band the recommender expects.
 * A recent graduate's experience is measured from graduation, so both answers use this.
 */
export function deriveSeniorityLevel(month: number, year: number, now: Date = new Date()): SeniorityLevel {
	const years = yearsSince(month, year, now);

	if (years < 1) return SENIORITY_LEVELS.entry;
	if (years < 3) return SENIORITY_LEVELS.junior;
	if (years < 6) return SENIORITY_LEVELS.mid;
	return SENIORITY_LEVELS.senior;
}
